import {
  Box,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { InputLabelProps } from '@mui/material/InputLabel/InputLabel';
import { SelectProps } from '@mui/material/Select/Select';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidV4 } from 'uuid';

import { NON_PRESENT_CONFIG } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalConfigSelectorProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelector/useGenericProposalConfigUtils';
import { ProposalConfigSelectorOptionDescriptionRow as OptionDescriptionRow } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelectorOptionDescriptionRow';
import { theme } from '~/theme';

const getLinkedInputLabelAndSelectProps = (fieldProps: {
  required?: boolean;
  label: ReactNode;
  selectId: string;
}): {
  inputLabelProps: Partial<InputLabelProps>;
  selectProps: Partial<SelectProps>;
} => {
  const { selectId, label, ...common } = fieldProps;

  const selectLabelId = `${selectId}Label`;

  return {
    inputLabelProps: {
      ...common,
      children: label,
      id: selectLabelId,
    },
    selectProps: {
      ...common,
      label,
      id: selectId,
      labelId: selectLabelId,
    },
  };
};

type PropsType<TConfig> = SelectProps &
  ProposalConfigSelectorProps<TConfig> & {
    FormControlProps?: FormControlProps;
    includeEmptyOption?: boolean;
    helperText?: string;
    onConfigChange: (hoursType: string | undefined) => void;
  };

export const ProposalConfigSelector = <TConfig,>(props: PropsType<TConfig>) => {
  const {
    value,
    options,
    typeEnums,
    getNonPresentConfig,
    selectedConfig,
    onSelectedConfigChange,
    onConfigChange,
    currency,
    FormControlProps,
    includeEmptyOption,
    helperText,
    children,
    onChange,
    convertConfigToOption,
    customOptionDescription,
    ...selectProps
  } = props;

  const { t } = useTranslation();
  const { label, required, id } = selectProps;
  const linkedComponentProps = getLinkedInputLabelAndSelectProps({
    label,
    selectId: id || uuidV4(),
    required,
  });

  const convertConfigToOptionOrUndefined = useCallback(
    (config?: TConfig) =>
      !!config ? convertConfigToOption(config) : undefined,
    [convertConfigToOption],
  );

  const handleSelect: SelectProps['onChange'] = useCallback(
    (event) => {
      const selectedTypeConfig = options.find(
        (option) => convertConfigToOption(option).id === event.target.value,
      );

      // NOTE: This component has dual action
      // 1) Selects a config that will be stored in a state, which is used elsewhere in form
      // 2) Updates the hoursType value in form data
      onSelectedConfigChange(selectedTypeConfig)
        .then((newConfig) => {
          onConfigChange(convertConfigToOptionOrUndefined(newConfig)?.type);
        })
        .catch((err) => {
          // TODO: Figure out how error should be displayed
          console.error(err);
        });
    },
    [
      onSelectedConfigChange,
      onConfigChange,
      options,
      convertConfigToOption,
      convertConfigToOptionOrUndefined,
    ],
  );

  const resolvedSelectProps = {
    ...selectProps,
    ...linkedComponentProps.selectProps,
    onChange: handleSelect,
    value: convertConfigToOptionOrUndefined(selectedConfig)?.id || '',
  };

  const getEnumTranslation = useCallback(
    (type: string) => {
      const foundEnum = typeEnums.find((e) => e.code === type);

      return !!foundEnum ? foundEnum.name : type;
    },
    [typeEnums],
  );

  const renderMenuItem = (option: TConfig) => {
    const { id, type, value, amount, info } = convertConfigToOption(option);

    const optionDescription = () => {
      if (!!customOptionDescription) {
        return customOptionDescription(option, currency);
      }

      const showAmountOrValue = () => {
        if (!!amount) {
          return (
            <OptionDescriptionRow>{`${amount} ${currency}`}</OptionDescriptionRow>
          );
        } else if (!!value) {
          return <OptionDescriptionRow>{value}</OptionDescriptionRow>;
        }
        return <></>;
      };

      return (
        <>
          {showAmountOrValue()}
          {!!info && <OptionDescriptionRow>{info}</OptionDescriptionRow>}
        </>
      );
    };

    return (
      <MenuItem value={id} key={id}>
        <Box>
          <Typography component={'span'} sx={{ display: 'block' }}>
            {getEnumTranslation(type)}
          </Typography>
          {id === NON_PRESENT_CONFIG ? (
            <OptionDescriptionRow color={theme.palette.error.main}>
              {t('selectedOptionNotAvailableSelectAnother')}
            </OptionDescriptionRow>
          ) : (
            optionDescription()
          )}
        </Box>
      </MenuItem>
    );
  };

  return (
    <FormControl {...FormControlProps}>
      <InputLabel {...linkedComponentProps.inputLabelProps} />
      <Select {...resolvedSelectProps}>
        {!!options && options.map(renderMenuItem)}
      </Select>
      {helperText && (
        <FormHelperText error={resolvedSelectProps.error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
